import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, finalize, map, shareReplay, tap, throwError } from 'rxjs';

import { GetRecordService } from '../../../data/record/get-record.service';
import { GetMediaService } from '../../../data/media/get-media.service';
import { DatabaseCacheService } from '../../../data/cache/database-cache.service';
import { TypesUtils, RecordData } from '../../../types';

@Injectable({ providedIn: 'root' })
export class MeasuredDataDetailsDataService {
  private readonly recordService = inject(GetRecordService);
  private readonly mediaService = inject(GetMediaService);
  private readonly cache = inject(DatabaseCacheService);

  private readonly recordSubject = new BehaviorSubject<RecordData | null>(null);
  readonly record$ = this.recordSubject.asObservable();

  private readonly mediaSubject = new BehaviorSubject<any | null>(null);
  readonly media$ = this.mediaSubject.asObservable();

  // In-flight de-dup. Without this, calling loadRecord(id) twice in quick
  // succession (e.g. route.params re-fires, MSAL silent-token races, multiple
  // subscribers awaiting different paths) fires the underlying HTTP twice.
  // The original implementation's shareReplay was inside a .pipe() that ran
  // on every call, so each invocation got its own Observable with its own
  // (un-shared) HTTP request. Sharing must happen at the *call-site* level.
  private readonly recordInflight = new Map<string, Observable<RecordData>>();
  private readonly mediaInflight = new Map<string, Observable<any>>();

  loadRecord(recordId: string): Observable<RecordData> {
    const sectionId = this.cache.getSelectedSection()?.sectionId;
    if (!sectionId) {
      return throwError(() => new Error('Section is not selected'));
    }

    const key = `${sectionId}|${recordId}`;
    const existing = this.recordInflight.get(key);
    if (existing) {
      return existing;
    }

    const shared = this.recordService.getRecord(recordId, sectionId).pipe(
      map(response => {
        const raw = response as any;
        const result = TypesUtils.convertJsonToRecords([raw], false);
        const record = result?.records?.[0] as RecordData | undefined;
        if (!record) {
          throw new Error(`RECORD_CONVERSION_FAILED: ${recordId}`);
        }
        // 生レスポンスの recordData.id（メディアドキュメントID）を保持する
        // 旧コードでは res['recordData'].id で getMedia に渡していた
        if (raw?.recordData?.id && record.data) {
          (record as any)._mediaDocumentId = raw.recordData.id;
        } else if (raw?.recordData && !raw.recordData.id) {
          // バックエンドのデータ異常: recordData は返るのに id が null のレコードが存在する。
          // この場合 getMedia は呼べない（レコードIDで代用すると /medias が 500 を返す）。
          console.warn(
            '[measured-data-details] recordWithData の recordData.id が空です。getMedia をスキップします。recordId:',
            record?.id,
          );
        }
        return record;
      }),
      tap(record => this.recordSubject.next(record)),
      finalize(() => this.recordInflight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.recordInflight.set(key, shared);
    return shared;
  }

  loadMedia(recordId: string): Observable<any> {
    const key = recordId;
    const existing = this.mediaInflight.get(key);
    if (existing) {
      return existing;
    }

    const shared = this.mediaService.getMedia(recordId).pipe(
      map((media: any) => {
        const mediaDocument = media?.[0] ?? null;
        if (!mediaDocument || typeof mediaDocument !== 'object') {
          return mediaDocument;
        }
        return {
          ...mediaDocument,
          __requestedMediaId: recordId,
        };
      }),
      tap(media => this.mediaSubject.next(media)),
      finalize(() => this.mediaInflight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.mediaInflight.set(key, shared);
    return shared;
  }
}
